import React, { useEffect, useState } from "react";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import { getUser } from "../../utils/session";

const cleanHtmlContent = (htmlContent) => {
  if (!htmlContent) return "";
  
  // More comprehensive approach to remove empty paragraph tags
  let cleanedContent = htmlContent;
  
  // Remove paragraphs that contain only whitespace, &nbsp;, or <br> tags
  // This regex matches paragraph tags and checks their content
  cleanedContent = cleanedContent.replace(/<p[^>]*>[\s\S]*?<\/p>/gi, (match) => {
    // Extract content between <p> and </p> tags
    const content = match.replace(/<p[^>]*>|<\/p>/gi, '');
    
    // Check if content is empty or contains only whitespace/&nbsp;/<br>
    const trimmedContent = content.trim();
    
    // If content is empty, only whitespace, only &nbsp;, only <br>, or combinations
    if (
      trimmedContent === '' ||
      trimmedContent === '&nbsp;' ||
      /^(\s|&nbsp;|<br\s*\/?>)*$/i.test(trimmedContent)
    ) {
      return ''; // Remove this paragraph
    }
    
    return match; // Keep this paragraph
  });
  
  return cleanedContent;
};

const CourseContentScreen = ({
  topic,
  currentPage,
  currentPageIndex,
  pages,
  timerShow,
  setTimerShow,
  backremainingTime,
  totalTime,
  currentCounterTimeRef,
  setCurrentPageIndex,
  handlePrevious,
  handleNext,
  handleExit,
  updateRemainingTime,
  lock,
  showChallengeModal,
  courseStatus
}) => {
  // Track if timer has completed to keep it visible
  const [timerCompleted, setTimerCompleted] = useState(false);
  
  // Check if course is completed (reading mode)
  const isCourseCompleted = courseStatus === '2' || (typeof window !== 'undefined' && JSON.parse(sessionStorage.getItem('user') || '{}').course_status === '2');
  
  // Check if operations should be disabled (security_verification_status === '1')
  const user = getUser();
  const shouldDisableOperations = user?.security_verification_status === '1';

  // Reset completed state if timer is restarted with remaining time > 0
  useEffect(() => {
    if (timerShow && backremainingTime !== null && backremainingTime > 0) {
      setTimerCompleted(false);
    }
  }, [timerShow, backremainingTime]);

  // Scroll to top when page changes
  useEffect(() => {
    // Scroll window to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Also scroll the content container to top if it exists
    const contentContainer = document.querySelector('.pageContent.contentDisplay');
    if (contentContainer) {
      contentContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Scroll parent scrollable containers
    const scrollableParent = contentContainer?.closest('.scrollSpy') || 
                            contentContainer?.closest('[data-bs-spy="scroll"]');
    if (scrollableParent) {
      scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPageIndex]);

  return (
    <div className="bg-white rounded-3 p-4 shadow mt-mb-20 mt-4 mb-5 course-content-screen">
      <div className="title border-bottom pb-3">
        <h5 className="h5 fw-bold">Section {topic.topic_code}: {topic.title}</h5>
      </div>
      {currentPage ? (
        <div
          className="pageContent contentDisplay mt-4"
          dangerouslySetInnerHTML={{
            __html: `<div className="pages-content"><div style=" display: flex; justify-content: space-between; align-items: center;">
              <h2 style="margin: 0;">${currentPage.title || (pages || [])[0].title}</h2>
              <span style="page-number margin: 0;">Page ${currentPageIndex+1} of ${(pages || []).length}</span>
            </div>` + cleanHtmlContent(currentPage.content)+`</div>`,
          }}
        />
      ) : (
        <></>
      )}

      <div className="container mt-4">
        {/* Inline controls (buttons hidden on mobile, timer repositioned via CSS) */}
        <div className="row mt-3 controls-row">
          <div className="col-lg-2 mt-4 exit-col">
            {timerShow == false ? (
              <button onClick={handleExit} className="btn btn-secondary-s mb-3 btn-md w-90 text-white">
                <i className="fa fa-arrow-left fs-12"></i> Exit
              </button>
            ) : (
              <button data-bs-toggle="modal" data-bs-target="#exampleModal" className="btn btn-secondary-s mb-3 btn-md w-90 text-white">
                <i className="fa fa-arrow-left fs-12"></i> Exit
              </button>
            )}
          </div>
          <div className="col-lg-8 col-md-6 prev-next-col">
            <div className="d-flex justify-content-between mt-4 gap-4">
              <button
                className="btn btn-primary-s text-white btn-md w-50 rounded-2"
                disabled={currentPageIndex === 0}
                onClick={handlePrevious}
              >
                <i className="fa fa-angle-left me-1"></i> PREVIOUS
              </button>

              <button
                className="btn btn-primary-s text-white btn-md w-50 rounded-2"
                disabled={
                  currentPageIndex + 1 === (pages || []).length &&
                  (shouldDisableOperations ||
                  (lock ||
                    (sessionStorage.getItem('topicStatus') !== '1' ||
                      (sessionStorage.getItem('topicStatus') === '1' && timerShow === true))))
                }
                onClick={handleNext}
              >
                NEXT <i className="fa fa-angle-right ms-1"></i>
              </button>
            </div>
          </div>
          <div className="col-lg-2 mt-3 timer-col">
            <div className="timer">
              {/* Show static completed timer when:
                  1. Timer is completed (timerCompleted === true)
                  2. Course is completed (isCourseCompleted === true)
                  3. Topic is completed (backremainingTime === 0, regardless of timerShow)
                  4. Timer is not in progress (timerShow === false AND backremainingTime is 0 or null)
              */}
              {(() => {
                // Check if topic is completed (backremainingTime === 0 means timer finished)
                const isTopicCompleted = backremainingTime === 0;
                // Check if timer is not running
                const isTimerNotRunning = timerShow === false;
                
                return (timerCompleted || 
                  isCourseCompleted ||
                  isTopicCompleted ||
                  (isTimerNotRunning && (backremainingTime === null || backremainingTime === 0)));
              })() ? (
                <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                  <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#029c6e"
                      strokeWidth="12"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#029c6e"
                      strokeWidth="12"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset="0"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    {(() => {
                      const minutes = Math.floor((totalTime % 3600) / 60);
                      const seconds = totalTime % 60;
                      const formattedMinutes = String(minutes).padStart(2, "0");
                      const formattedSeconds = String(seconds).padStart(2, "0");
                      return `${formattedMinutes}:${formattedSeconds}`;
                    })()}
                  </div>
                </div>
              ) : (backremainingTime !== null && backremainingTime > 0 && timerShow) ? (
                <CountdownCircleTimer
                  isPlaying={!timerCompleted && timerShow && !showChallengeModal}
                  duration={totalTime}
                  isGrowing={true}
                  initialRemainingTime={timerCompleted ? 0 : backremainingTime}
                  onUpdate={(remainingTime) => {
                    // Only update if timer is playing (not paused by modal)
                    if (!showChallengeModal) {
                      currentCounterTimeRef.current = remainingTime;
                    }
                    if (remainingTime === 0 && !timerCompleted && !showChallengeModal) {
                      setTimerCompleted(true);
                      setTimerShow(false);
                      updateRemainingTime();
                    }
                  }}
                  size={100}
                  colors={showChallengeModal ? "#868585" : "#0BA8CF"}
                >
                  {({ remainingTime }) => {
                    const elapsedTime = totalTime - remainingTime;
                    const minutes = Math.floor((elapsedTime % 3600) / 60);
                    const seconds = elapsedTime % 60;
                    const formattedMinutes = String(minutes).padStart(2, "0");
                    const formattedSeconds = String(seconds).padStart(2, "0");
                    return `${formattedMinutes}:${formattedSeconds}`;
                  }}
                </CountdownCircleTimer>
              ) : (
                <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                  <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#029c6e"
                      strokeWidth="12"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="#029c6e"
                      strokeWidth="12"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset="0"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#333'
                  }}>
                    {(() => {
                      const minutes = Math.floor((totalTime % 3600) / 60);
                      const seconds = totalTime % 60;
                      const formattedMinutes = String(minutes).padStart(2, "0");
                      const formattedSeconds = String(seconds).padStart(2, "0");
                      return `${formattedMinutes}:${formattedSeconds}`;
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile fixed bottom bar for navigation buttons and timer */}
      <div
        className="d-md-none fixed-bottom bg-white border-top shadow-sm"
        style={{ minHeight: timerShow ? "120px" : "56px" }}
      >
        <div className="container py-2 px-3">
          {/* Timer centered below buttons on mobile */}
          {(timerShow && backremainingTime !== null) ? (
            <div className="d-flex justify-content-center align-items-center">
              <CountdownCircleTimer
                isPlaying={!timerCompleted}
                duration={totalTime}
                isGrowing={true}
                initialRemainingTime={timerCompleted ? 0 : backremainingTime}
                onUpdate={(remainingTime) => {
                  currentCounterTimeRef.current = remainingTime;
                  if (remainingTime === 0 && !timerCompleted) {
                    setTimerCompleted(true);
                    setTimerShow(false);
                    updateRemainingTime();
                  }
                }}
                size={80}
                colors="#0BA8CF"
              >
                {({ remainingTime }) => {
                  const elapsedTime = totalTime - remainingTime;
                  const minutes = Math.floor((elapsedTime % 3600) / 60);
                  const seconds = elapsedTime % 60;
                  const formattedMinutes = String(minutes).padStart(2, "0");
                  const formattedSeconds = String(seconds).padStart(2, "0");
                  return `${formattedMinutes}:${formattedSeconds}`;
                }}
              </CountdownCircleTimer>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <div style={{ position: 'relative', width: '60px', height: '60px' }}>
                <svg width="60" height="60" style={{ transform: 'rotate(-90deg)' }}>
                  <circle
                    cx="30"
                    cy="30"
                    r="24"
                    fill="none"
                    stroke="#029c6e"
                    strokeWidth="8"
                  />
                  <circle
                    cx="30"
                    cy="30"
                    r="24"
                    fill="none"
                    stroke="#029c6e"
                    strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 24}`}
                    strokeDashoffset="0"
                    strokeLinecap="round"
                  />
                </svg>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  {(() => {
                    const minutes = Math.floor((totalTime % 3600) / 60);
                    const seconds = totalTime % 60;
                    const formattedMinutes = String(minutes).padStart(2, "0");
                    const formattedSeconds = String(seconds).padStart(2, "0");
                    return `${formattedMinutes}:${formattedSeconds}`;
                  })()}
                </div>
              </div>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
            <button
              className="btn btn-secondary-s text-white btn-sm"
              onClick={handleExit}
              disabled={timerShow !== false}
            >
              <i className="fa fa-arrow-left fs-12"></i> Exit
            </button>
            <div className="d-flex flex-grow-1 justify-content-end gap-2">
              <button
                className="btn btn-primary-s text-white btn-sm"
                disabled={currentPageIndex === 0}
                onClick={handlePrevious}
              >
                <i className="fa fa-angle-left me-1"></i> Previous
              </button>
              <button
                className="btn btn-primary-s text-white btn-sm"
                disabled={
                  currentPageIndex + 1 === (pages || []).length &&
                  (shouldDisableOperations ||
                  (lock ||
                    (sessionStorage.getItem("topicStatus") !== "1" ||
                      (sessionStorage.getItem("topicStatus") === "1" &&
                        timerShow === true))))
                }
                onClick={handleNext}
              >
                Next <i className="fa fa-angle-right ms-1"></i>
              </button>
            </div>
          </div>
          
        </div>
      </div>

      {/* Mobile-only typography, spacing, button size, and controls layout tweaks */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media (max-width: 767.98px) {
              .course-content-screen {
                font-size: 1rem;
                padding: 1.25rem;
                margin-top: 1rem;
                margin-bottom: 1.5rem;
                padding-bottom: 8rem; /* space for bottom bar with timer */
              }
              .course-content-screen h5 {
                font-size: 1.1rem;
              }
              .course-content-screen h2 {
                font-size: 1.2rem;
              }
              .course-content-screen .pageContent {
                margin-top: 0.75rem !important;
              }
              .course-content-screen .pageContent img {
                max-width: 80% !important;
                height: auto !important;
                display: block;
                margin: 0.75rem auto;
              }
              .course-content-screen .btn {
                padding: 0.35rem 0.6rem;
                font-size: 1rem;
              }
              .course-content-screen .row.mt-3 {
                margin-top: 0.75rem !important;
              }
              .course-content-screen .mt-4 {
                margin-top: 0.85rem !important;
              }
              .course-content-screen .mb-5 {
                margin-bottom: 1.5rem !important;
              }
              /* On mobile, hide all inline controls (buttons and timer) */
              .course-content-screen .controls-row {
                display: none;
              }
            }
          `,
        }}
      />
    </div>
  );
};

export default CourseContentScreen;
